import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import { billingGetConversationUsage } from '@api/billing';
import type { ConversationUsageSummary, UsageDetailResponse } from '@api/billing';
import MessageRequestGroup from './MessageRequestGroup';
import {
  GroupContainer,
  GroupHeader,
  HeaderLeft,
  DateRange,
  UserName,
  HeaderRight,
  CreditsBadge,
  MessageCount,
  SourceBadge,
  ExpandIcon,
  DetailContainer,
  DetailLoading,
  LegacyTable,
  LegacyRow,
  LegacyDate,
  LegacyAmount,
  RetryLink,
} from './ConversationGroup.styled';

function formatDateRange(first: string, last: string): string {
  const firstDate = new Date(first);
  const lastDate = new Date(last);
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  if (firstDate.toDateString() === lastDate.toDateString()) {
    const dateStr = firstDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    return `${dateStr}, ${firstDate.toLocaleTimeString(undefined, timeOpts)} — ${lastDate.toLocaleTimeString(undefined, timeOpts)}`;
  }
  return `${firstDate.toLocaleString(undefined, opts)} — ${lastDate.toLocaleString(undefined, opts)}`;
}

function formatLegacyDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  summary: ConversationUsageSummary;
}

const ConversationGroup: React.FC<Props> = ({ summary }) => {
  const intl = useIntl();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const toggle = () => setExpanded((prev) => !prev);

  const detailQuery = useQuery<UsageDetailResponse>({
    queryKey: ['conversationUsage', user?.id, summary.conversationId],
    queryFn: () => billingGetConversationUsage(user!.token!, summary.conversationId),
    enabled: expanded && !!user?.token,
  });

  const legacyTransactions = detailQuery.data?.legacyTransactions ?? [];

  return (
    <GroupContainer data-testid="conversation-group">
      <GroupHeader onClick={toggle} type="button" aria-expanded={expanded}>
        <HeaderLeft>
          <DateRange>
            {formatDateRange(summary.firstActivityAt, summary.lastActivityAt)}
          </DateRange>
          {summary.userName && <UserName>{summary.userName}</UserName>}
        </HeaderLeft>
        <HeaderRight>
          <MessageCount>
            {intl.formatMessage(
              { id: 'billing.usage.messages', defaultMessage: '{count, plural, one {# message} other {# messages}}' },
              { count: summary.messageCount },
            )}
          </MessageCount>
          <CreditsBadge>
            {summary.totalCredits.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            {' '}
            {intl.formatMessage({ id: 'billing.usage.credits', defaultMessage: 'credits' })}
          </CreditsBadge>
          {summary.sourceApp.split(',').map((src) => (
            <SourceBadge key={src}>{src.trim()}</SourceBadge>
          ))}
          <ExpandIcon $expanded={expanded}>▼</ExpandIcon>
        </HeaderRight>
      </GroupHeader>

      {expanded && (
        <DetailContainer>
          {detailQuery.isLoading && (
            <DetailLoading>
              {intl.formatMessage({ id: 'billing.usage.loadingDetail', defaultMessage: 'Loading details...' })}
            </DetailLoading>
          )}
          {detailQuery.isError && (
            <DetailLoading>
              {intl.formatMessage({ id: 'billing.usage.loadFailed', defaultMessage: 'Failed to load details.' })}
              {' '}
              <RetryLink type="button" onClick={() => detailQuery.refetch()}>
                {intl.formatMessage({ id: 'billing.usage.retry', defaultMessage: 'Retry' })}
              </RetryLink>
            </DetailLoading>
          )}
          {detailQuery.data?.messageRequests.map((req) => (
            <MessageRequestGroup key={req.messageRequestId} detail={req} />
          ))}
          {legacyTransactions.length > 0 && (
            <LegacyTable data-testid="legacy-usage-table">
              {legacyTransactions.map((tx) => (
                <LegacyRow key={tx.id}>
                  <LegacyDate>{formatLegacyDate(tx.createdAt)}</LegacyDate>
                  <LegacyAmount>
                    {tx.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                    {' '}
                    {intl.formatMessage({ id: 'billing.usage.credits', defaultMessage: 'credits' })}
                  </LegacyAmount>
                </LegacyRow>
              ))}
            </LegacyTable>
          )}
        </DetailContainer>
      )}
    </GroupContainer>
  );
};

export default ConversationGroup;
